import { useTranslation } from 'react-i18next';
import { formatCelsius } from '../../utils/thermal/legend';

/*
 * One ROI's temperatures in the 32 bins of its analysis's palette range
 * (thermal/roi.py histogram, utils/thermal/roi.js; plan phase 10): a bar per
 * bin, low to high from left to right whatever the page's direction, with the
 * pixels below and above the range said in words beside it. The bars are a
 * picture of numbers that are all in its label: the range, how many pixels,
 * how many below and above it.
 *
 *   histogram  {lo_c, hi_c, counts: [32], under, over}, or null (an ROI saved
 *              before migration 0102 has none: nothing is drawn)
 */
export default function RoiHistogram({ histogram, colour = 'var(--c-primary)', width = 160, height = 36, testId }) {
  const { t } = useTranslation('thermal');
  if (!histogram || !Array.isArray(histogram.counts) || !histogram.counts.length) return null;
  const { counts, under = 0, over = 0, lo_c: lo, hi_c: hi } = histogram;
  const most = Math.max(1, ...counts);
  const inRange = counts.reduce((a, b) => a + b, 0);
  const bar = width / counts.length;
  const range = `${formatCelsius(lo, 1)} – ${formatCelsius(hi, 1)}`;
  return (
    <figure style={figureStyle} data-testid={testId}>
      <svg role="img" viewBox={`0 0 ${width} ${height}`} width={width} height={height} style={{ display: 'block' }}
           aria-label={t('histogram.label', { range, pixels: inRange + under + over, under, over })}>
        <line x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} stroke="var(--c-border-strong)" strokeWidth={1} />
        {counts.map((c, i) => {
          const h = c ? Math.max(1, (c / most) * (height - 2)) : 0;
          return <rect key={i} x={i * bar + 0.5} y={height - 1 - h} width={Math.max(0.5, bar - 1)} height={h}
                       fill={colour} data-count={c} />;
        })}
      </svg>
      <figcaption style={captionStyle}>
        <bdi dir="ltr">{range}</bdi>
        {under > 0 && <span data-testid={testId && `${testId}-under`}> · {t('histogram.under', { n: under })}</span>}
        {over > 0 && <span data-testid={testId && `${testId}-over`}> · {t('histogram.over', { n: over })}</span>}
      </figcaption>
    </figure>
  );
}

const figureStyle = { margin: 0, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' };
const captionStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)' };
